import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarCheck, Users, Rocket, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";

const BENEFITS = [
  "Live-walk the workspace with an RA/QA lead on your call",
  "Pilot Qualmitta on one of your active submissions",
  "Get a security review + DPA turnaround inside 24h",
  "See MAUDE, FAERS, USPTO and openFDA live",
];

const SIZES = ["Just me", "2-5", "6-15", "16-50", "50+"];

export default function Demo() {
  const [f, setF] = useState({ name: "", email: "", company: "", role: "", team_size: "", product_focus: "", use_case: "", preferred_time: "", honeypot: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/demo/requests", f);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send — email hello@qualmitta.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-slate-900">
      <Navbar />
      <section className="mx-auto grid max-w-[1200px] gap-12 px-6 py-16 lg:grid-cols-12 lg:px-10 lg:py-24">
        <div className="lg:col-span-5">
          <div className="mb-4 inline-flex items-center gap-2 border border-slate-300 bg-white px-3 py-1.5">
            <CalendarCheck className="h-4 w-4 text-[#2563EB]" />
            <span className="overline text-slate-600">BOOK A DEMO</span>
          </div>
          <h1 className="font-serif text-5xl font-semibold leading-[1.05] tracking-tight lg:text-6xl">
            See Qualmitta on
            <br />
            <span className="italic text-slate-600">your submission.</span>
          </h1>
          <p className="mt-6 text-slate-600">
            Book a 30-minute demo. We'll walk through the workspace with your device or therapeutic —
            drafting, predicates, MAUDE/FAERS, QMS, Risk, Design Controls — and answer every
            security / procurement question you send us in advance.
          </p>

          <div className="mt-8 space-y-3">
            {BENEFITS.map((b) => (
              <div key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]" strokeWidth={2} />
                <span>{b}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-6 border-t border-slate-200 pt-8 text-xs text-slate-500">
            <div className="flex items-center gap-2"><Users className="h-4 w-4 text-[#2563EB]" /> Built for RA, QA, R&D</div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#2563EB]" /> DPA on request</div>
            <div className="flex items-center gap-2"><Rocket className="h-4 w-4 text-[#2563EB]" /> Pilot in a week</div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div data-testid="demo-thanks" className="flex h-full flex-col items-start justify-center border border-slate-200 bg-white p-10">
              <CheckCircle2 className="mb-4 h-8 w-8 text-emerald-600" strokeWidth={1.5} />
              <h3 className="font-serif text-3xl font-semibold text-slate-900">Thanks — we're on it.</h3>
              <p className="mt-3 max-w-md text-slate-600">
                We'll reply within one business day with two proposed times and any DPA / security
                docs you asked for. Meanwhile, feel free to <a className="text-[#2563EB] underline" href="/workspace">explore the workspace</a>.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="demo-form" className="border border-slate-200 bg-white p-6 lg:p-8">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Name *" testid="demo-name" value={f.name} onChange={set("name")} />
                <Field label="Work email *" testid="demo-email" value={f.email} onChange={set("email")} type="email" />
                <Field label="Company" testid="demo-company" value={f.company} onChange={set("company")} />
                <Field label="Your role" testid="demo-role" value={f.role} onChange={set("role")} placeholder="RA / QA / Founder / Engineer" />
                <Select label="Team size" testid="demo-team-size" value={f.team_size} onChange={set("team_size")} options={SIZES} />
                <Select label="Product focus" testid="demo-focus" value={f.product_focus} onChange={set("product_focus")} options={["Medical device", "Drug", "Both", "Combination product"]} />
              </div>
              <div className="mt-4">
                <Label>Preferred time</Label>
                <input data-testid="demo-preferred-time" value={f.preferred_time} onChange={set("preferred_time")} placeholder="e.g. weekday afternoons US Eastern, week of Sep 15" className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
              </div>
              <div className="mt-4">
                <Label>What are you trying to submit?</Label>
                <textarea data-testid="demo-use-case" value={f.use_case} onChange={set("use_case")} rows={4} placeholder="510(k) for our CGM v2 / IND for our GLP-1 program / EU MDR CER refresh…" className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
              </div>
              {/* honeypot */}
              <input tabIndex="-1" autoComplete="off" value={f.honeypot} onChange={set("honeypot")} className="absolute -left-[9999px]" aria-hidden="true" />

              <button type="submit" data-testid="demo-submit" disabled={loading} className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-[#2563EB] px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-40 md:w-auto">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />}
                {loading ? "Sending…" : "Request my demo"}
              </button>
              <p className="mt-4 text-[11px] text-slate-500">By requesting a demo you agree to be contacted by the Qualmitta team. We don't sell or share your details.</p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Label({ children }) { return <div className="overline mb-1 text-slate-500">{children}</div>; }
function Field({ label, testid, ...p }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <input data-testid={testid} {...p} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
    </label>
  );
}
function Select({ label, testid, options = [], ...p }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <select data-testid={testid} {...p} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900">
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
